// React
import React from 'react';
import {useState, useEffect, useRef} from 'react';

// MUI
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';

// others
import './App.css';
import { useFetch } from './hooks/useFetch.jsx';
import RenameDialog, {RenameIconButton, hoverRenameSx} from './RenameDialog.jsx';
import AdvancedSection, {techDetailsSx} from './properties/AdvancedSection.jsx';

const ERR_CONTROLLER_UNREACHABLE = "The controller is not responding. Please check that it is connected to wifi.";
const ERR_UNKNOWN_ERROR = "The request failed.";
const OTA_POLL_INTERVAL_MS = 500;
const OTA_RESULT_INFO = {
    success: "Firmware updated \u2014 the controller is rebooting. Please do not power it off during the reboot.",
    refused: "The controller is already running the latest firmware. No update was applied.",
};

export default function Controllers(props) {
    const [controllers, setControllers] = useState({});
    const [selectedController, setSelectedController] = useState(null);
    // rename dialog target: {kind, entityId, initialName}
    const [renameTarget, setRenameTarget] = useState(null);
    const [restartLoading, setRestartLoading] = useState(false);
    const [otaLoading, setOtaLoading] = useState(false);
    // otaRequestId != null means a push has been dispatched and is being
    // polled. otaProgress carries the per-controller snapshot returned by
    // the last poll (phase/sent/total/result/error).
    const [otaRequestId, setOtaRequestId] = useState(null);
    const [otaProgress, setOtaProgress] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("error");
    const fetchWrapper = useFetch();
    const pollRef = useRef(null);
    // ensures that the latest fetchWrapper with current access_token is used, in case of token expiry
    const fetchWrapperRef = useRef(fetchWrapper);
    fetchWrapperRef.current = fetchWrapper;

    useEffect(() => {
        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/controllers/userid`, {})
            .then(response => {
                if (response.ok)
                    return response.json();
                else if (response.status === 504)
                    throw new Error(ERR_CONTROLLER_UNREACHABLE);
                else
                    return response.json();
            })
            .then(responseObj => {
                // sample response: {"controllers":[{"id":4,"name":"Greenhouse Controller"}, ...]}
                console.log("get controllers body = " + JSON.stringify(responseObj));
                var myControllers = {};
                if (responseObj.controllers) {
                    for (const controller of responseObj.controllers) {
                        myControllers[""+controller.id] = {
                            id: controller.id,
                            name: controller.name
                        };
                    }
                }
                setControllers(myControllers);
            })
            .catch(error => {
                console.log("error fetching controllers = " + JSON.stringify(error));
                setSnackbarMessage(error.message || ERR_UNKNOWN_ERROR);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleClick(controller) {
        setSelectedController(controller);
        // Switching controllers stops visibility polling for the previous
        // controller's in-flight OTA, if any. The OTA itself continues on
        // the controller (it was dispatched as a goroutine on the server);
        // only the per-controller progress view is reset for the new
        // selection.
        if (otaRequestId) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            setOtaRequestId(null);
            setOtaProgress(null);
            setOtaLoading(false);
        }
    }

    function showSnackbar(message, severity) {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }

    function handleRenamed(newName) {
        if (!renameTarget) {
            return;
        }
        const key = ""+renameTarget.entityId;
        setControllers(prev => {
            if (!prev[key]) {
                return prev;
            }
            return {...prev, [key]: {...prev[key], name: newName}};
        });
        setSelectedController(prev => {
            if (!prev || prev.id !== renameTarget.entityId) {
                return prev;
            }
            return {...prev, name: newName};
        });
    }

    function handleSnackbarClose(event, reason) {
        setSnackbarOpen(false);
    }

    function restartController(e) {
        if (!selectedController) return;
        const restartBody = {
            controllerIds: [selectedController.id]
        };
        setRestartLoading(true);
        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/controllers/restart`, restartBody)
            .then(response => {
                if (response.ok)
                    return response.json();
                else if (response.status === 504)
                    throw new Error(ERR_CONTROLLER_UNREACHABLE);
                else if (response.status === 409)
                    throw new Error("A restart or firmware update is already in progress for this controller. Please wait for it to finish.");
                else if (response.status === 403)
                    throw new Error("You do not have permission to restart this controller.");
                else
                    return response.json();
            })
            .then(responseObj => {
                console.log("restart body = " + JSON.stringify(responseObj));
                showSnackbar("Restart initiated successfully. The controller will reboot momentarily.", "success");
            })
            .catch(error => {
                console.log("error = " + JSON.stringify(error));
                showSnackbar(error.message || ERR_UNKNOWN_ERROR, "error");
            })
            .finally(() => {
                setRestartLoading(false);
            });
    }

    function updateFirmware(e) {
        if (!selectedController) return;
        // The backend defaults firmwarePath to /firmware/latest.bin when empty,
        // and reads the offered version from the /firmware/latest.version sidecar
        // (version: 0 here means "not supplied; use the sidecar"). The controller
        // refuses the push if its running firmware is already >= that version.
        const otaBody = {
            controllerIds: [selectedController.id],
            firmwarePath: "",
            version: 0
        };
        setOtaLoading(true);
        setOtaProgress(null);
        setOtaRequestId(null);
        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/ota/push`, otaBody)
            .then(response => {
                if (response.ok)
                    return response.json();
                else if (response.status === 504)
                    throw new Error(ERR_CONTROLLER_UNREACHABLE);
                else if (response.status === 409)
                    throw new Error("A firmware update or restart is already in progress for this controller. Please wait for it to finish.");
                else if (response.status === 403)
                    throw new Error("You do not have permission to update this controller's firmware.");
                else if (response.status === 400)
                    return response.json().then(body => { throw new Error(body && body.error_description ? body.error_description : "No firmware file is available to push."); });
                else
                    return response.json();
            })
            .then(responseObj => {
                console.log("ota push body = " + JSON.stringify(responseObj));
                if (!responseObj.requestId) {
                    // No requestId means the server accepted the push on the
                    // shared-secret path (shouldn't happen for a JWT caller,
                    // but handle it gracefully): no progress to poll.
                    showSnackbar("Firmware update started. The controller will apply the update and reboot when complete.", "success");
                    setOtaLoading(false);
                    return;
                }
                // Begin polling for progress. otaLoading stays true until
                // the poll reports done (see the poll effect).
                setOtaRequestId(responseObj.requestId);
            })
            .catch(error => {
                console.log("error = " + JSON.stringify(error));
                showSnackbar(error.message || ERR_UNKNOWN_ERROR, "error");
                setOtaLoading(false);
            });
    }

    // Polling effect: when otaRequestId is set, poll GET /ota/status/{id}
    // every OTA_POLL_INTERVAL_MS. Stop (clear the interval) when the
    // request reports done, or on a 404/403. Progress is mirrored into
    // otaProgress; on done, the otaLoading button is released.
    useEffect(() => {
        if (!otaRequestId) {
            return;
        }
        let cancelled = false;
        // allow a few retries before giving up
        let consecutiveErrors = 0;
        const MAX_CONSECUTIVE_ERRORS = 10; // 10 × 500ms = 5s of failures
        const tick = async () => {
            try {
                const response = await fetchWrapperRef.current.get(`https://api.${process.env.REACT_APP_DOMAIN}/ota/status/${otaRequestId}`);
                if (cancelled) return;
                if (response.status === 404) {
                    // Request unknown (server restarted, GC'd, or stale id).
                    stopPolling();
                    setOtaProgress({ phase: "done", result: "failed", error: "The update was interrupted (its progress is no longer tracked). Please try again." });
                    setOtaLoading(false);
                    setOtaRequestId(null);
                    return;
                }
                if (!response.ok) {
                    // 403 (shouldn't happen for the caller's own request) or
                    // any other non-2xx. Surface a generic failure.
                    stopPolling();
                    setOtaProgress({ phase: "done", result: "failed", error: "Firmware update progress check failed." });
                    setOtaLoading(false);
                    setOtaRequestId(null);
                    return;
                }
                const body = await response.json();
                if (cancelled) return;
                // Successful poll — reset the error counter.
                consecutiveErrors = 0;
                // The UI sends one controller per click; pick the matching
                // entry from controllers[] (fall back to the first).
                let entry = null;
                if (Array.isArray(body.controllers)) {
                    if (selectedController) {
                        entry = body.controllers.find(c => c.controllerId === selectedController.id) || body.controllers[0];
                    } else {
                        entry = body.controllers[0];
                    }
                }
                setOtaProgress(entry || { phase: body.done ? "done" : "init" });
                if (body.done) {
                    stopPolling();
                    setOtaLoading(false);
                    setOtaRequestId(null);
                }
            } catch (err) {
                if (cancelled) return;
                consecutiveErrors++;
                console.log("poll error = " + JSON.stringify(err) + " (consecutive: " + consecutiveErrors + ")");
                if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                    // Sustained poll failures — likely the auth session has lapsed and silent renewal 
                    //  isn't producing a valid token, or the server is unreachable. Stop spinning and
                    //  surface an error instead of freezing the progress bar silently.
                    stopPolling();
                    setOtaProgress({ phase: "done", result: "failed", error: "Connection to the server was lost during the update. The firmware transfer may still be running on the controller — please check back in a few minutes." });
                    setOtaLoading(false);
                    setOtaRequestId(null);
                    return;
                }
                // Below threshold: leave the interval running so a momentary
                // blip (e.g. token renewal gap) doesn't kill the progress view.
            }
        };
        const stopPolling = () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
        // Kick off an immediate first poll so the UI shows "init" right away,
        // then start the interval.
        tick();
        pollRef.current = setInterval(tick, OTA_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otaRequestId]);

    function renderControllerList() {
        var tempArray = [];
        Object.keys(controllers).forEach(function(key, index) {
            var controller = controllers[key];
            tempArray.push(
                <ListItem key={""+controller.id} disablePadding sx={hoverRenameSx} secondaryAction={
                    <RenameIconButton title={controller.name} onClick={() => setRenameTarget({kind:'controller', entityId: controller.id, initialName: controller.name})}/>
                }>
                    <ListItemButton selected={selectedController && selectedController.id === controller.id} sx={{position:'relative', height:'40px', lineHeight:'40px', alignContent:'center', borderBottom:'1px solid', borderTop:'1px solid', borderColor:'grey.300'}} onClick={() => handleClick(controller)}>
                        <ListItemText primary={controller.name}/>
                    </ListItemButton>
                </ListItem>
            );
        });
        if (tempArray.length === 0) {
            return (<Typography sx={{padding:2, color:'text.secondary'}}>No controllers found.</Typography>);
        }
        return tempArray;
    }

    function renderSelectedControllerDetails() {
            if (!selectedController) {
                return (
                    <Box sx={{padding:2}}>
                        <Typography color="text.secondary">Select a controller to view its details and actions.</Typography>
                    </Box>
                );
            }
            return (
                <Box sx={{padding:2, display:'flex', flexDirection:'column', gap:2}}>
                    <Box>
                        <Box sx={{display:'flex', alignItems:'center', gap:1, ...hoverRenameSx}}>
                            <Typography variant="h6" align='left'>{selectedController.name}</Typography>
                            <RenameIconButton title={selectedController.name} onClick={() => setRenameTarget({kind:'controller', entityId: selectedController.id, initialName: selectedController.name})}/>
                        </Box>
                        <AdvancedSection title="Technical details" sx={techDetailsSx}>
                            <Typography align='left' variant="body2" color="text.secondary">Controller ID: {selectedController.id}</Typography>
                        </AdvancedSection>
                    </Box>
                    <Divider />
                    <Box sx={{display:'flex', flexDirection:'column', gap:1, maxWidth:'400px'}}>
                        <Box sx={{display:'flex', flexDirection:'column', gap:1}}>
                            <LoadingButton loading={restartLoading} variant="contained" color="primary" onClick={restartController}>
                                Restart Controller
                            </LoadingButton>
                            <Typography variant="body2" color="text.secondary" align='left'>
                                Reboots the controller. Useful if it becomes unresponsive or you want to apply configuration changes.
                            </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{display:'flex', flexDirection:'column', gap:1}}>
                            <LoadingButton loading={otaLoading} variant="contained" color="secondary" onClick={updateFirmware} disabled={otaRequestId !== null}>
                                Update Firmware
                            </LoadingButton>
                            <Typography variant="body2" color="text.secondary" align='left'>
                                Installs the latest firmware from the server. If the controller is already running the latest version, the update is skipped automatically.
                            </Typography>
                            {renderOtaProgress()}
                        </Box>
                    </Box>
                </Box>
            );
        }

    // renderOtaProgress renders the live update state: an "init" indeterminate
    // bar while waiting on the device, a determinate chunk counter while
    // chunking, a "keep page open" advisory while in flight, and a terminal
    // status line once done. Returns null when no push has been initiated.
    function renderOtaProgress() {
        if (!otaProgress && !otaRequestId) return null;
        const p = otaProgress || { phase: "init" };
        const inFlight = !p.result; // phase init / chunking / complete
        // Linear progress value: chunking uses sent/total; init/complete use
        // indeterminate (represented by passing undefined → MUI indeterminate).
        let progressEl = null;
        if (p.phase === "chunking" && p.total) {
            const pct = Math.min(100, Math.round((p.sent / p.total) * 100));
            progressEl = (
                <Box sx={{display:'flex', alignItems:'center', gap:1}}>
                    <Box sx={{flexGrow:1}}>
                        <LinearProgress variant="determinate" value={pct} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace:'nowrap'}}>
                        {p.sent}/{p.total}
                    </Typography>
                </Box>
            );
        } else if (inFlight) {
            progressEl = <LinearProgress variant="indeterminate" />;
        }

        // Advisory while the update is in flight: ask the user not to refresh
        // or navigate away, since progress doesn't survive a page reload.
        let advisoryEl = null;
        if (inFlight && otaRequestId) {
            advisoryEl = (
                <Alert severity="info" variant="outlined" sx={{mt:1, '& .MuiAlert-message':{fontSize:'0.85rem'}}}>
                    Please keep this page open. If you refresh or navigate away, the update continues on the controller but you will no longer see its progress here.
                </Alert>
            );
        }

        // Terminal status line.
        let statusEl = null;
        if (p.result === "success") {
            statusEl = <Alert severity="success" variant="outlined" sx={{mt:1}}>{OTA_RESULT_INFO.success}</Alert>;
        } else if (p.result === "refused") {
            statusEl = <Alert severity="info" variant="outlined" sx={{mt:1}}>{OTA_RESULT_INFO.refused}</Alert>;
        } else if (p.result === "failed") {
            statusEl = <Alert severity="error" variant="outlined" sx={{mt:1}}>{"Update failed: " + (p.error || "unknown error")}</Alert>;
        } else if (p.phase === "init") {
            statusEl = <Typography variant="body2" color="text.secondary" align="left" sx={{mt:1}}>Preparing firmware update…</Typography>;
        } else if (p.phase === "chunking") {
            statusEl = <Typography variant="body2" color="text.secondary" align="left" sx={{mt:1}}>{"Updating firmware… (chunk " + p.sent + " of " + p.total + ")"}</Typography>;
        } else if (p.phase === "complete") {
            statusEl = <Typography variant="body2" color="text.secondary" align="left" sx={{mt:1}}>Finalizing update on the controller…</Typography>;
        }

        return (
            <Box>
                {progressEl}
                {advisoryEl}
                {statusEl}
            </Box>
        );
    }

    return (

        <Box className="Controllers" sx={{overflow:'hidden', display:'flex', flexDirection:'row', width:'100%', height:'100%', borderTop:'1px solid', borderColor:'grey.300' }}>
            
            {/* left bar - controller list */}
            <List sx={{overflow:'auto', width:'20%', height:'100%', borderRight:'1px solid', borderColor:'grey.300'}}>
                {renderControllerList()}
            </List>
            
            
            {/* right pane - controller details */}
            <Box sx={{overflow:'auto', flexGrow:1, height:'100%', borderRight:'1px solid', borderColor:'grey.300'}}>
                {renderSelectedControllerDetails()}
            </Box>

            {renameTarget ?
                <RenameDialog
                    open={!!renameTarget}
                    entityKind={renameTarget.kind}
                    entityId={renameTarget.entityId}
                    initialName={renameTarget.initialName}
                    onClose={() => setRenameTarget(null)}
                    onRenamed={handleRenamed}
                /> : null}

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert severity={snackbarSeverity} variant="outlined" sx={{ width: '100%', bgcolor: 'background.paper' }} >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
            
        </Box>
    );
}
